/**
 * Interval analysis for recurring transaction detection
 * Handles interval calculation, consistency checking, and frequency detection
 */

import { average } from '../../utils/array-helpers';
import { DETECTION_CONFIG, type RecurringFrequency } from './config';

/**
 * Calculate days between two dates
 */
export function daysBetween(date1: string, date2: string): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Result of interval analysis
 */
export interface IntervalAnalysis {
  /** Raw intervals between consecutive transactions */
  intervals: number[];

  /** Average interval across all transactions */
  avgInterval: number;

  /** Normalized intervals (accounting for skipped payments) */
  normalizedIntervals: number[];

  /** Whether skipped payments were detected */
  hasSkippedPayments: boolean;

  /** Detected frequency (weekly, monthly, etc.) */
  frequency: RecurringFrequency | null;

  /** Whether intervals are consistent enough to be recurring */
  isConsistent: boolean;

  /** Whether a reactivation gap was detected (cancelled then resumed) */
  hasReactivation: boolean;

  /** Indices of reactivation gaps in the intervals array */
  reactivationGapIndices: number[];

  /** Breakdown of interval types for debugging */
  breakdown: {
    normal: number[];
    skipped: number[];
    tooFrequent: number[];
    reactivation: number[];
  };
}

/**
 * Calculate and analyze intervals between transaction dates
 *
 * @param dates - Array of transaction dates (will be sorted)
 * @returns Comprehensive interval analysis
 */
export function analyzeIntervals(dates: string[]): IntervalAnalysis {
  if (dates.length < 2) {
    return {
      intervals: [],
      avgInterval: 0,
      normalizedIntervals: [],
      hasSkippedPayments: false,
      frequency: null,
      isConsistent: false,
      hasReactivation: false,
      reactivationGapIndices: [],
      breakdown: { normal: [], skipped: [], tooFrequent: [], reactivation: [] }
    };
  }

  // Sort dates chronologically
  const sortedDates = [...dates].sort((a, b) =>
    new Date(a).getTime() - new Date(b).getTime()
  );

  // Calculate raw intervals
  const intervals: number[] = [];
  for (let i = 1; i < sortedDates.length; i++) {
    intervals.push(daysBetween(sortedDates[i - 1], sortedDates[i]));
  }

  // Detect potential reactivation gaps
  const reactivationGapIndices = detectReactivationGaps(intervals);
  const hasReactivation = reactivationGapIndices.length > 0;

  // Calculate average excluding reactivation gaps
  const nonReactivationIntervals = intervals.filter((_, idx) =>
    !reactivationGapIndices.includes(idx)
  );
  const avgInterval = nonReactivationIntervals.length > 0
    ? average(nonReactivationIntervals)
    : average(intervals);

  // Detect and normalize skipped payments
  const { normalizedIntervals, hasSkippedPayments } = normalizeSkippedPayments(intervals);

  // Use normalized average if we detected skipped payments and it looks monthly
  const normalizedAvg = average(normalizedIntervals);
  const effectiveAvg = (hasSkippedPayments && normalizedAvg >= 20 && normalizedAvg <= 35)
    ? normalizedAvg
    : avgInterval;

  // Detect frequency
  const frequency = detectFrequency(effectiveAvg);

  // Check consistency
  const { isConsistent, breakdown } = checkIntervalConsistency(
    intervals,
    frequency,
    avgInterval,
    reactivationGapIndices
  );

  return {
    intervals,
    avgInterval: Math.round(avgInterval),
    normalizedIntervals,
    hasSkippedPayments,
    frequency,
    isConsistent,
    hasReactivation,
    reactivationGapIndices,
    breakdown
  };
}

/**
 * Detect reactivation gaps: Large gaps that indicate a subscription was
 * cancelled and then reactivated (e.g., Netflix cancelled for 6 months, then resumed)
 *
 * @example
 * Peacock: 30, 31, 30, 31, [208 days gap], 30, 31, 30
 * Without reactivation detection: rejected as inconsistent
 * With reactivation detection: accepted as monthly with one reactivation
 *
 * @param intervals - Array of intervals between transactions
 * @returns Indices of intervals that are reactivation gaps
 */
function detectReactivationGaps(intervals: number[]): number[] {
  const config = DETECTION_CONFIG.reactivationDetection;

  if (!config.enabled || intervals.length < config.minBeforeGap + config.minAfterGap + 1) {
    return [];
  }

  const reactivationIndices: number[] = [];

  for (let i = 0; i < intervals.length; i++) {
    const interval = intervals[i];

    // Check if this interval is large enough to be a reactivation gap
    if (interval < config.minGapDays) {
      continue;
    }

    // Check if we have enough intervals before this gap
    if (i < config.minBeforeGap) {
      continue;
    }

    // Check if we have enough intervals after this gap
    if (i >= intervals.length - config.minAfterGap) {
      continue;
    }

    // Get intervals before and after the gap
    const beforeGap = intervals.slice(Math.max(0, i - 3), i);
    const afterGap = intervals.slice(i + 1, Math.min(intervals.length, i + 4));

    // Calculate average interval before and after (excluding the gap)
    const avgBefore = average(beforeGap);
    const avgAfter = average(afterGap);

    // Check if intervals before and after are similar (suggesting same pattern)
    // Allow 20% variance between before and after averages
    const variance = Math.abs(avgBefore - avgAfter) / Math.max(avgBefore, avgAfter);

    if (variance <= 0.20 && avgBefore >= 20 && avgBefore <= 40) {
      // This looks like a reactivation gap
      reactivationIndices.push(i);

      // Only allow one reactivation gap per pattern
      if (reactivationIndices.length >= config.maxReactivations) {
        break;
      }
    }
  }

  return reactivationIndices;
}

/**
 * Detect skipped payments: If a subscription normally charges monthly (30 days)
 * but has an interval of ~60 or ~90 days, treat it as 2 or 3 skipped cycles
 * rather than an inconsistent pattern.
 *
 * @example
 * Netflix charges Jan 1, Feb 1, May 1 (90 days)
 * Without normalization: intervals = [31, 90] → rejected as inconsistent
 * With normalization: intervals = [31, 30] → accepted as monthly
 */
function normalizeSkippedPayments(intervals: number[]): {
  normalizedIntervals: number[];
  hasSkippedPayments: boolean;
} {
  const config = DETECTION_CONFIG.skipPaymentDetection;
  const normalizedIntervals: number[] = [];
  let hasSkippedPayments = false;

  intervals.forEach(interval => {
    // Check if this looks like a multiple of monthly (~30 days)
    if (interval >= config.minDays && interval <= config.maxDays) {
      // 2x or 3x monthly (60 or 90 days with tolerance)
      const multiplier = Math.round(interval / config.baseInterval);
      normalizedIntervals.push(interval / multiplier);
      hasSkippedPayments = true;
    } else {
      normalizedIntervals.push(interval);
    }
  });

  return { normalizedIntervals, hasSkippedPayments };
}

/**
 * Detect frequency based on average interval
 *
 * @param avgInterval - Average days between transactions
 * @returns Detected frequency or null if no pattern matches
 */
export function detectFrequency(avgInterval: number): RecurringFrequency | null {
  const frequencies = DETECTION_CONFIG.frequencies;

  for (const [key, range] of Object.entries(frequencies)) {
    if (avgInterval >= range.min && avgInterval <= range.max) {
      return range.label;
    }
  }

  return null;
}

/**
 * Check if intervals are consistent enough to be considered recurring
 * Allows for natural variation (month lengths, weekends, etc.)
 *
 * @param intervals - Raw intervals between transactions
 * @param frequency - Detected frequency
 * @param avgInterval - Average interval
 * @param reactivationGapIndices - Indices of reactivation gaps to exclude from consistency check
 * @returns Whether intervals are consistent and breakdown of interval types
 */
function checkIntervalConsistency(
  intervals: number[],
  frequency: RecurringFrequency | null,
  avgInterval: number,
  reactivationGapIndices: number[] = []
): {
  isConsistent: boolean;
  breakdown: { normal: number[]; skipped: number[]; tooFrequent: number[]; reactivation: number[] };
} {
  if (!frequency || intervals.length === 0) {
    return {
      isConsistent: false,
      breakdown: { normal: [], skipped: [], tooFrequent: [], reactivation: [] }
    };
  }

  // Determine expected range based on frequency
  const { expectedMin, expectedMax } = getExpectedRange(frequency, avgInterval);

  // Categorize each interval
  const normalIntervals: number[] = [];
  const skippedPayments: number[] = [];
  const tooFrequent: number[] = [];
  const reactivationGaps: number[] = [];

  intervals.forEach((interval, idx) => {
    // Check if this is a reactivation gap
    if (reactivationGapIndices.includes(idx)) {
      reactivationGaps.push(interval);
      return;
    }

    // Categorize based on expected range
    if (interval < expectedMin) {
      tooFrequent.push(interval);
    } else if (interval >= expectedMin && interval <= expectedMax) {
      normalIntervals.push(interval);
    } else if (interval > expectedMax && interval < avgInterval * 3) {
      skippedPayments.push(interval);
    }
  });

  const maxSkipped = DETECTION_CONFIG.thresholds.maxSkippedPayments;

  // Pattern is consistent if:
  // 1. No intervals are too short (prevents daily transactions)
  // 2. Most intervals are in normal range (excluding reactivation gaps)
  // 3. Up to N skipped payments allowed
  const nonReactivationCount = intervals.length - reactivationGaps.length;
  const minNormalCount = Math.min(3, Math.max(1, nonReactivationCount - 2));
  const isConsistent =
    tooFrequent.length === 0 &&
    normalIntervals.length >= minNormalCount &&
    skippedPayments.length <= maxSkipped;

  return {
    isConsistent,
    breakdown: {
      normal: normalIntervals,
      skipped: skippedPayments,
      tooFrequent: tooFrequent,
      reactivation: reactivationGaps
    }
  };
}

/**
 * Get expected interval range for a frequency
 * Accounts for natural variations (month lengths, weekends, etc.)
 */
function getExpectedRange(
  frequency: RecurringFrequency,
  avgInterval: number
): { expectedMin: number; expectedMax: number } {
  const frequencies = DETECTION_CONFIG.frequencies;

  switch (frequency) {
    case 'weekly':
      return { expectedMin: frequencies.weekly.min, expectedMax: frequencies.weekly.max };
    case 'biweekly':
      return { expectedMin: frequencies.biweekly.min, expectedMax: frequencies.biweekly.max };
    case 'monthly':
      // Monthly: allow 25-35 days (accounts for Feb, 31-day months, and billing cycle variations)
      return { expectedMin: frequencies.monthly.min, expectedMax: frequencies.monthly.max };
    case 'quarterly':
      return { expectedMin: frequencies.quarterly.min, expectedMax: frequencies.quarterly.max };
    case 'yearly':
      return { expectedMin: frequencies.yearly.min, expectedMax: frequencies.yearly.max };
    default:
      // Default: ±20% of average
      return {
        expectedMin: avgInterval * 0.8,
        expectedMax: avgInterval * 1.2
      };
  }
}
